import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpRequest } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface UploadProgress {
  percent: number;
  fileName: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  message?: string;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
  fileName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Upload de thumbnail de curso
   */
  uploadCourseThumbnail(courseId: string, file: File): Observable<UploadResult> {
    const formData = new FormData();
    formData.append('thumbnailFile', file);

    return this.upload(`${this.apiUrl}/course/thumbnail/${courseId}`, formData, 'thumbnail');
  }

  /**
   * Upload de foto de perfil de professor
   */
  uploadTeacherProfilePhoto(teacherId: string, file: File): Observable<UploadResult> {
    const formData = new FormData();
    formData.append('profilePhoto', file);

    return this.upload(`${this.apiUrl}/teacher/profile-photo/${teacherId}`, formData, 'profilePhoto');
  }

  /**
   * Upload genérico com progresso
   */
  upload(
    url: string,
    formData: FormData,
    fieldName: string = 'file'
  ): Observable<UploadResult> {
    return new Observable(subscriber => {
      const req = new HttpRequest('POST', url, formData, {
        reportProgress: true
      });

      this.http.request(req).subscribe({
        next: (event: HttpEvent<any>) => {
          if (event.type === HttpEventType.Response) {
            subscriber.next({
              success: true,
              url: event.body?.url || event.body?.thumbnailUrl || event.body?.profilePhotoUrl,
              fileName: event.body?.fileName
            });
            subscriber.complete();
          }
        },
        error: (error) => {
          subscriber.next({
            success: false,
            error: this.extractErrorMessage(error)
          });
          subscriber.complete();
        }
      });
    });
  }

  /**
   * Valida arquivo antes do upload
   */
  validateFile(file: File, options: {
    maxSizeMB?: number;
    allowedTypes?: string[];
  } = {}): { valid: boolean; error?: string } {
    const { maxSizeMB = 15, allowedTypes = ['image/*'] } = options;

    // Verifica tamanho
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `Arquivo muito grande. Máximo: ${maxSizeMB}MB`
      };
    }

    // Verifica tipo
    const isAllowed = allowedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.replace('/*', ''));
      }
      return file.type === type;
    });

    if (!isAllowed) {
      return {
        valid: false,
        error: `Tipo de arquivo não permitido. Aceitos: ${allowedTypes.join(', ')}`
      };
    }

    return { valid: true };
  }

  /**
   * Extrai mensagem de erro da resposta
   */
  private extractErrorMessage(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'Erro ao realizar upload';
  }
}
